import { Component } from "react";

export type ErrorBoundaryProps = {
  onCatch?: () => void;
  fallback: React.ReactNode;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Simple error boundary for catching React render errors. For things that we want to
 * report to sentry (most things), use `Sentry.ErrorBoundary` instead
 * @noInheritDoc
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch() {
    const { onCatch = () => null } = this.props;
    onCatch();
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      return fallback;
    }

    return children;
  }
}

export default ErrorBoundary;
